import { DateFragment, DateTime, Duration, DurationTime, Utc } from "@tento-chrono";

import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";

/**
 * Mutable per-drag state used by every gesture in
 * `existing-event-gestures` / `new-event-gestures`.
 *
 *   - `initialTimeRange` — the event's range at the moment the
 *     gesture started (resize / move anchor).
 *   - `currentTimeRange` — the running proposed range as the cursor
 *     moves; the gesture writes here on every `onPointerMove` and
 *     reads from here on commit.
 *   - `currentDf` — the DateFragment the cursor is currently over;
 *     updates when the drag crosses a column boundary so the gesture
 *     resolves position against the receiving column.
 */
export class CalendarGestureState {
  currentDf: DateFragment;
  currentTimeRange: DateTime.Range<Utc>;

  constructor(
    readonly info: GestureInfo,
    readonly initialTimeRange: DateTime.Range<Utc>,
  ) {
    this.currentDf = info.windowed.fragment;
    this.currentTimeRange = initialTimeRange;
  }

  get originalDf(): DateFragment {
    return this.info.windowed.fragment;
  }

  static fromLocation(
    info: GestureInfo,
    position: DurationTime,
  ): CalendarGestureState {
    const start = info.windowed.fragment.start.withTime(
      position.floor(Duration.Time.MIN15).toTimeOfDayWrapping(),
    );
    const initialTimeRange = new DateTime.Range(
      start,
      start.add(Duration.Time.hrs(1)),
    ).toUtc();
    return new CalendarGestureState(info, initialTimeRange);
  }

  didChange(): boolean {
    return !this.currentTimeRange.eq(this.initialTimeRange);
  }
}
